import React, { useMemo, useState, useEffect, useRef } from 'react';
import {
  BarChart,
  useNexus,
  Spinner,
  useMediaQuery,
} from '@spotoninc/nexus-react';
import { Box, Flex, Text } from '@chakra-ui/react';
import '../../../styles/consolidated-bar-chart.css';

// Helper function to deeply compare arrays
const areArraysEqual = (arr1, arr2) => {
  if (!arr1 || !arr2) return arr1 === arr2;
  if (arr1.length !== arr2.length) return false;

  for (let i = 0; i < arr1.length; i++) {
    if (arr1[i] !== arr2[i]) return false;
  }

  return true;
};

// Helper function to compare data objects
const isDataEqual = (prevData, nextData) => {
  if (!prevData || !nextData) return prevData === nextData;

  return (
    areArraysEqual(prevData.data, nextData.data) &&
    areArraysEqual(prevData.series, nextData.series) &&
    areArraysEqual(prevData.categories, nextData.categories)
  );
};

// Custom comparison function for React.memo
const arePropsEqual = (prevProps, nextProps) => {
  // Compare income, expense, revenue, and categories
  return (
    isDataEqual(prevProps.income, nextProps.income) &&
    isDataEqual(prevProps.expense, nextProps.expense) &&
    isDataEqual(prevProps.revenue, nextProps.revenue) &&
    areArraysEqual(prevProps.categories, nextProps.categories)
  );
};

/**
 * Consolidated Bar Chart component using Nexus BarChart
 * Shows profit, expense, and revenue data in a single chart
 */
const ConsolidatedBarChart = ({ income, expense, revenue, categories }) => {
  // Add loading state
  const [isLoading, setIsLoading] = useState(true);
  // Add state to track if we should show "No Data" message
  const [showNoData, setShowNoData] = useState(false);

  // Refs to store previous data values
  const prevIncomeRef = useRef(null);
  const prevExpenseRef = useRef(null);
  const prevRevenueRef = useRef(null);
  const prevCategoriesRef = useRef(null);

  // Ref to track if data has actually changed
  const dataChangedRef = useRef(false);

  // Get Nexus theme tokens for consistent styling
  const {
    theme: { tokens },
  } = useNexus();

  // Detect if we're on a mobile device (max-width: 768px)
  const isMobile = useMediaQuery('(max-width: 768px)');

  // Check if data has actually changed
  useEffect(() => {
    const incomeChanged = !isDataEqual(prevIncomeRef.current, income);
    const expenseChanged = !isDataEqual(prevExpenseRef.current, expense);
    const revenueChanged = !isDataEqual(prevRevenueRef.current, revenue);
    const categoriesChanged = !areArraysEqual(
      prevCategoriesRef.current,
      categories
    );

    // Only set dataChanged to true if any of the data has actually changed
    dataChangedRef.current =
      incomeChanged || expenseChanged || revenueChanged || categoriesChanged;

    // Update refs with current values
    prevIncomeRef.current = income;
    prevExpenseRef.current = expense;
    prevRevenueRef.current = revenue;
    prevCategoriesRef.current = categories;
  }, [income, expense, revenue, categories]);

  // Calculate total values for each series to display in the legend
  const calculateTotal = data => {
    if (!data || !Array.isArray(data)) return 0;
    return data.reduce((sum, value) => sum + (Number(value) || 0), 0);
  };

  const incomeTotal = calculateTotal(income?.data);
  const expenseTotal = calculateTotal(expense?.data);
  const revenueTotal = calculateTotal(revenue?.data);

  // Format currency for legend
  const formatCurrency = value => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  // Prepare series data for the chart with custom colors
  const series = useMemo(
    () => [
      {
        type: 'column',
        data: income?.data || [],
        name: `Profit (${formatCurrency(incomeTotal)})`,
        color: '#4287f5', // Blue color for Profit
        stack: 'profit', // Assign a unique stack ID to prevent stacking
      },
      {
        type: 'column',
        data: revenue?.data || [],
        name: `Revenue (${formatCurrency(revenueTotal)})`,
        color: '#20b2aa', // Teal color for Revenue
        stack: 'revenue', // Assign a unique stack ID to prevent stacking
      },
      {
        type: 'column',
        data: expense?.data || [],
        name: `Expense (${formatCurrency(expenseTotal)})`,
        color: '#9370db', // Purple color for Expense
        stack: 'expense', // Assign a unique stack ID to prevent stacking
      },
    ],
    // Use only the necessary dependencies
    [
      income?.data,
      expense?.data,
      revenue?.data,
      incomeTotal,
      expenseTotal,
      revenueTotal,
    ]
  );

  // Filter out series with empty data - memoize to prevent recreation on each render
  const filteredSeries = useMemo(
    () => series.filter(s => s.data && s.data.length > 0),
    [series]
  );

  // Check if we have empty data or only zeros
  const hasEmptyOrZeroData = useMemo(() => {
    // Check if income data is empty or only contains zeros
    const isIncomeEmpty =
      !income?.data ||
      income.data.length === 0 ||
      (income.data.length === 1 && Number(income.data[0]) === 0);

    // Check if expense data is empty or only contains zeros
    const isExpenseEmpty =
      !expense?.data ||
      expense.data.length === 0 ||
      (expense.data.length === 1 && Number(expense.data[0]) === 0);

    // Check if revenue data is empty or only contains zeros
    const isRevenueEmpty =
      !revenue?.data ||
      revenue.data.length === 0 ||
      (revenue.data.length === 1 && Number(revenue.data[0]) === 0);

    // Return true if all data sets are empty or only contain zeros
    return isIncomeEmpty && isExpenseEmpty && isRevenueEmpty;
  }, [income, expense, revenue]);

  // Reset loading state when data changes (e.g., when location changes)
  useEffect(() => {
    // Check if we have actual data (not initial empty state)
    const hasActualData =
      (income?.data && income.data.length > 0 && income.data[0] !== 0) ||
      (expense?.data && expense.data.length > 0 && expense.data[0] !== 0) ||
      (revenue?.data && revenue.data.length > 0 && revenue.data[0] !== 0);

    if (hasActualData) {
      // We have real data - stop loading immediately
      setIsLoading(false);
      setShowNoData(false);
    } else {
      // No data yet - keep showing spinner
      setIsLoading(true);
      setShowNoData(false);
    }
  }, [income, expense, revenue]); // Reset when data changes

  // Add a failsafe timer to prevent infinite loading
  useEffect(() => {
    // Set a maximum loading time of 5 seconds
    const failsafeTimer = setTimeout(() => {
      // If we're still loading after 5 seconds, check if we truly have no data
      if (isLoading && hasEmptyOrZeroData) {
        setIsLoading(false);
        setShowNoData(true);
      }
    }, 5000); // Reduced from 10s to 5s

    return () => clearTimeout(failsafeTimer);
  }, [isLoading, hasEmptyOrZeroData]);

  // Memoize chart configuration objects to prevent unnecessary re-renders
  const legendConfig = useMemo(
    () => ({
      itemStyle: {
        color: tokens.semanticColorTextNeutralStrongestNone,
        fontSize: `${tokens.primitiveFontSize75}`,
        fontWeight: `${tokens.primitiveFontWeightMedium}`,
        lineHeight: tokens.fontLineHeight200,
        letterSpacing: '0.0125rem',
        fontFamily: 'Poppins, sans-serif',
      },
    }),
    [tokens]
  );

  // Memoize tooltip configuration
  const tooltipConfig = useMemo(
    () => ({
      valuePrefix: '$',
      valueDecimals: 2,
      shared: true,
      backgroundColor: '#FFFFFF',
      borderWidth: 0,
      borderRadius: 12,
      shadow: true,
      useHTML: true,
      formatter: function () {
        // Get the x-axis category (month)
        const category = this.x;

        // Start building the tooltip HTML
        let tooltipHtml = `
        <div class="tooltip-container">
          <div class="tooltip-header">
            ${category}
          </div>
      `;

        // Add each data point
        this.points.forEach(function (point) {
          const seriesName = point.series.name;
          const color = point.series.color;
          const value = point.y;
          // Format negative values with minus sign before the dollar sign
          let formattedValue;
          if (value < 0) {
            formattedValue =
              '-$' +
              Math.abs(value).toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              });
          } else {
            formattedValue =
              '$' +
              value.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              });
          }

          tooltipHtml += `
          <div class="tooltip-row">
            <div class="tooltip-label-container">
              <div class="tooltip-color-indicator" style="background-color: ${color};"></div>
              <span class="tooltip-label">${seriesName.split(' ')[0]}</span>
            </div>
            <span class="tooltip-value">${formattedValue}</span>
          </div>
        `;
        });

        // Close the tooltip container
        tooltipHtml += '</div>';

        return tooltipHtml;
      },
    }),
    []
  );

  // Memoize x-axis configuration
  const xAxisConfig = useMemo(
    () => ({
      labels: {
        style: {
          color: `${tokens.semanticColorTextNeutralLightestNone}`,
          fontSize: `${tokens.primitiveFontSize75}`,
          fontWeight: `${tokens.primitiveFontWeightRegular}`,
          lineHeight: `${tokens.primitiveFontLineHeight200}`,
          letterSpacing: `${tokens.primitiveFontLetterSpacingWide}`,
          fontFamily: `${tokens.primitiveFontFamilySans}`,
        },
      },
    }),
    [tokens]
  );

  // Memoize y-axis configuration
  const yAxisConfig = useMemo(
    () => ({
      allowDecimals: false,
      labels: {
        formatter: function () {
          // Format negative values with minus sign before the dollar sign
          const value = this.value;
          const absValue = Math.abs(value);

          // Use abbreviations for all values to save space
          if (absValue >= 1000000) {
            // For values >= 1M, use 'M' abbreviation
            const formattedValue = (absValue / 1000000).toFixed(1) + 'M';
            return value < 0 ? '-$' + formattedValue : '$' + formattedValue;
          } else if (absValue >= 1000) {
            // For values >= 1K, use 'K' abbreviation
            const formattedValue = (absValue / 1000).toFixed(0) + 'K';
            return value < 0 ? '-$' + formattedValue : '$' + formattedValue;
          }

          // Standard formatting for small values
          if (value < 0) {
            return '-$' + absValue.toLocaleString();
          }
          return '$' + absValue.toLocaleString();
        },
        align: 'right',
        // Add more space between labels and increase padding
        padding: 8,
        distance: 10,
        style: {
          color: `${tokens.semanticColorTextNeutralLightestNone}`,
          fontSize: `${tokens.primitiveFontSize75}`,
          fontWeight: `${tokens.primitiveFontWeightRegular}`,
          lineHeight: `${tokens.primitiveFontLineHeight200}`,
          letterSpacing: `${tokens.primitiveFontLetterSpacingWide}`,
          fontFamily: `${tokens.primitiveFontFamilySans}`,
        },
        // Ensure labels don't overlap by allowing them to stagger if needed
        staggerLines: 1,
        // Limit label length to prevent overlap
        overflow: 'allow',
      },
      // Ensure negative values are displayed and axis is evenly spaced
      startOnTick: true,
      endOnTick: true,
      gridLineDashStyle: 'Dot',
      gridLineWidth: 1,
      // Strictly limit the number of ticks
      tickAmount: 6,
      tickPositioner: function () {
        // Get the actual data min and max
        const dataMin = this.dataMin;
        const dataMax = this.dataMax;

        // Determine the range
        const range = dataMax - dataMin;

        // Target number of ticks (including min and max)
        // Fewer ticks means less chance of overlap
        const targetTickCount = 5;

        // Calculate a nice, rounded interval that will give us approximately the target number of ticks
        let tickInterval = Math.pow(10, Math.floor(Math.log10(range)));

        // Adjust the interval to get closer to our target tick count
        if (range / tickInterval > targetTickCount) {
          // If we have too many ticks, increase the interval
          tickInterval *= 2;
        } else if (range / tickInterval < targetTickCount / 2) {
          // If we have too few ticks, decrease the interval
          tickInterval /= 2;
        }

        // For very small ranges, ensure we don't create too many ticks
        if (tickInterval < 1) {
          tickInterval = 1;
        }

        // Round the interval to a nice number (1, 2, 5, 10, 20, 50, etc.)
        const magnitude = Math.pow(10, Math.floor(Math.log10(tickInterval)));
        const normalized = tickInterval / magnitude;

        if (normalized < 1.5) {
          tickInterval = magnitude;
        } else if (normalized < 3) {
          tickInterval = 2 * magnitude;
        } else if (normalized < 7) {
          tickInterval = 5 * magnitude;
        } else {
          tickInterval = 10 * magnitude;
        }

        // Calculate min and max values that are multiples of the tick interval
        let min, max;

        // Handle negative values
        if (dataMin < 0) {
          // Round down to the next multiple of tickInterval below dataMin
          min = Math.floor(dataMin / tickInterval) * tickInterval;
        } else {
          // For positive values, start at 0 or the floor of dataMin
          min =
            dataMin > tickInterval
              ? Math.floor(dataMin / tickInterval) * tickInterval
              : 0;
        }

        // Round up to the next multiple of tickInterval above dataMax
        max = Math.ceil(dataMax / tickInterval) * tickInterval;

        // Generate evenly spaced positions
        const positions = [];
        for (let pos = min; pos <= max; pos += tickInterval) {
          positions.push(pos);
        }

        // If we still have too many ticks, reduce them by taking every other one
        if (positions.length > targetTickCount + 2) {
          // Recalculate the tick interval based on the target tick count
          const newTickInterval = (max - min) / targetTickCount;
          // Regenerate evenly spaced tick positions
          const newPositions = [];
          for (let pos = min; pos <= max; pos += newTickInterval) {
            newPositions.push(Math.round(pos * 100) / 100); // Round to avoid floating-point errors
          }
          return newPositions;
        }

        return positions;
      },
      // Ensure zero line is visible
      plotLines: [
        {
          value: 0,
          width: 1,
          color: '#808080',
        },
      ],
    }),
    [tokens, isMobile]
  );

  // Memoize chart options
  const chartOptions = useMemo(
    () => ({
      type: 'column',
      animation: true,
      style: {
        fontFamily: 'Poppins, sans-serif',
      },
      // Add margin to ensure y-axis labels have enough space
      marginLeft: 80, // Increase left margin for y-axis labels
      marginRight: 10,
      spacingLeft: 10,
      spacingRight: 10,
    }),
    []
  );

  // Memoize plot options
  const plotOptions = useMemo(
    () => ({
      column: {
        grouping: true, // Enable grouping (side by side bars)
        groupPadding: 0.15, // Space between groups
        pointPadding: 0.1, // Space between bars in a group
        borderWidth: 0,
        shadow: false,
        minPointLength: 3, // Ensure very small values are still visible
      },
    }),
    []
  );

  // If no data is available or there's an API error, show a message
  if (
    filteredSeries.length === 0 ||
    !categories ||
    categories.length === 0 ||
    isLoading
  ) {
    // This block is for rendering the loading state or no data message
    return (
      <Flex
        className="chart-no-data-container"
        justifyContent="center"
        alignItems="center"
        height="400px"
        width="100%"
      >
        {showNoData ? (
          <Text
            fontSize="lg"
            fontWeight="medium"
            color={tokens.semanticColorTextNeutralStrongestNone}
          >
            No Data
          </Text>
        ) : (
          <Spinner size={40} color={tokens.semanticColorIconBrandStrongNone} />
        )}
      </Flex>
    );
  }

  return (
    <Box className="chart-container">
      <BarChart
        className="consolidated-bar-chart"
        series={filteredSeries}
        categories={categories}
        showLegend={true}
        legendVerticalAlign="top"
        legendHorizontalAlign="left"
        legend={legendConfig}
        yAxisTickInterval={2000}
        yAxisLabelFormat="$value"
        scrollableAreaMinWidth={500}
        tooltip={tooltipConfig}
        chartHeight={400}
        xAxis={xAxisConfig}
        yAxis={yAxisConfig}
        chart={chartOptions}
        plotOptions={plotOptions}
      />
    </Box>
  );
};

// Export the memoized component with custom comparison function
export default React.memo(ConsolidatedBarChart, arePropsEqual);
